import { LearnDataVideo } from '@osmosis/shared/types/learn';
import { QuestionSection } from '@osmosis/shared/types/learndb/learnData';
import { TrackBreadcrumbHandler } from '@osmosis/shared/types/learn';
import { UserProps } from '@osmosis/shared/types/user';

import { track } from '../util/tracking';

export type LearnVideoTrackingVideo = Pick<
  LearnDataVideo,
  'isPrimeOnly' | 'upNext' | 'breadcrumbsv2' | 'learnNodeID' | 'name'
> & {
  flashcards: LearnDataVideo['sections']['flashcards'];
  questions: LearnDataVideo['sections']['questions'];
};

/**
 * HandleTrackBreadcrumb..
 * @param source
 * @returns {TrackBreadcrumbHandler}
 */
export const getBreadcrumbTrackHandler = (
  source = '',
): TrackBreadcrumbHandler => {
  return async (breadcrumbName?: string, from?: string) => {
    const crumbClicked = breadcrumbName?.replace(/\s+/g, '-').toLowerCase();
    const full_breadcrumb = from?.replace(/\//g, '|') || '';
    await track(
      'content_breadcrumb_click',
      {
        source,
        crumb_clicked: crumbClicked,
        full_breadcrumb:
          full_breadcrumb[0] === '|'
            ? full_breadcrumb.slice(1)
            : full_breadcrumb,
      },
      ['keen'],
    );
  };
};

/**
 * LearnVideoTracking.
 */
export const learnVideoTracking = async (
  video: LearnVideoTrackingVideo,
  user: UserProps | null,
) => {
  if (!video) {
    return;
  }
  const {
    isPrimeOnly,
    upNext,
    breadcrumbsv2,
    learnNodeID,
    name,
    flashcards,
    questions,
  } = video;

  let trackData: Record<string, any> = {
    version: 'reorg',
    concept: name,
    flashcards: flashcards?.list?.length || 0,
    questions: getQuestionCount(questions),
    questions_step1: questions?.['step1']?.list?.length || 0,
    questions_step2: questions?.['step2']?.list?.length || 0,
    has_up_next: upNext?.title,
    is_prime: !!isPrimeOnly,
    hasPrime: user?.prime ? user.prime.status > 0 : false,
    learnNodeID,
    platform: 'web-app',
    loggedOut: !user,
  };
  const breadcrumb =
    breadcrumbsv2?.[0]?.link
      ?.split('/')
      .filter(Boolean)
      .map((breadcrumb) => breadcrumb.toLowerCase().replace(' ', '-')) || [];

  if (breadcrumb.length) {
    const contentShelf = breadcrumb.reduce((crumbs, current, index) => {
      crumbs[`content_shelf_l${index + 1}`] = current;

      return crumbs;
    }, {} as Record<string, string>);
    trackData = {
      ...trackData,
      ...contentShelf,
      breadcrumbs: breadcrumb.join('|'),
      organ_system: breadcrumb[1] || '',
    };
  }
  track('open_concept_card', trackData, ['keen', 'pendo']);
  if (!user) {
    track(
      'show_learn_page_lockout',
      {
        type: 'immediate-prime-video-lockout',
      },
      ['keen', 'pendo'],
    );
  }
};

function getQuestionCount(questions?: QuestionSection) {
  let result = 0;
  if (!questions) {
    return result;
  }

  const keys = Object.keys(questions);
  result = new Set(
    keys.reduce((acc, key) => {
      return [...acc, ...(questions[key]?.list || [])];
    }, [] as number[]),
  ).size;

  return result;
}
